/**
 * ContinuityReadinessPanel — dark-theme panel used inside the Continuity System
 * admin tab. Shows aggregate Continuity System readiness across ALL users.
 *
 * Privacy contract: numbers only, no per-user drill-down. The underlying
 * encrypted content stays readable only to each owner.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const headers = () => {
  const t = localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const CARD_TINTS = {
  neutral: { bg: 'bg-gray-900', border: 'border-gray-700', val: 'text-gray-100', lbl: 'text-gray-400' },
  emerald: { bg: 'bg-emerald-900/20', border: 'border-emerald-700/50', val: 'text-emerald-200', lbl: 'text-emerald-400' },
  amber:   { bg: 'bg-amber-900/20',   border: 'border-amber-700/50',   val: 'text-amber-200',   lbl: 'text-amber-400' },
  rose:    { bg: 'bg-rose-900/20',    border: 'border-rose-700/50',    val: 'text-rose-200',    lbl: 'text-rose-400' },
};

const Card = ({ label, value, tint = 'neutral', testKey }) => {
  const c = CARD_TINTS[tint] || CARD_TINTS.neutral;
  return (
    <div
      className={`border ${c.border} ${c.bg} rounded px-3 py-2 min-w-[8.5rem]`}
      data-testid={`crs-card-${testKey}`}
    >
      <div className={`text-xl font-semibold ${c.val}`}>{value}</div>
      <div className={`text-[10px] uppercase tracking-wide ${c.lbl}`}>{label}</div>
    </div>
  );
};

const ContinuityReadinessPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/continuity/summary`, { headers: headers() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const t = (data && data.asset_totals) || {};

  return (
    <div className="bg-gray-800 rounded-xl p-4 border border-gray-700" data-testid="continuity-readiness-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div>
            <h3 className="text-white font-semibold">Continuity Status</h3>
            <p className="text-xs text-gray-400">Counts only — no per-user drill-down. The encrypted content stays readable only to each owner.</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs text-gray-300 hover:text-white border border-gray-600 rounded px-2 py-1 flex items-center gap-1"
          data-testid="crs-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      {error ? (
        <p className="text-xs text-rose-300">Couldn&rsquo;t load: {error}</p>
      ) : !data ? (
        <p className="text-xs text-gray-500">Loading&hellip;</p>
      ) : (
        <div className="flex flex-wrap gap-2 items-stretch">
          <Card label="Continuity users" value={data.continuity_user_count} tint="emerald" testKey="continuity-users" />
          <Card label="Assets tracked"   value={t.total || 0}               tint="neutral" testKey="assets-tracked" />
          <Card label="Complete"         value={t.complete || 0}            tint="emerald" testKey="complete" />
          <Card label="In progress"      value={t.in_progress || 0}         tint="amber"   testKey="in-progress" />
          <Card label="At risk"          value={t.at_risk || 0}             tint="rose"    testKey="at-risk" />
          <Card label="Users all-complete" value={data.users_all_complete}  tint="emerald" testKey="users-all-complete" />
          <Card label={`Idle >${data.idle_days_threshold || 90}d`} value={data.users_idle} tint="neutral" testKey="users-idle" />
        </div>
      )}
    </div>
  );
};

export default ContinuityReadinessPanel;
